/**
 * I18n Configuration
 * 
 * Vue I18n v11 setup with TypeScript support
 */

import { createI18n } from 'vue-i18n';
import en from './locales/en';
import ru from './locales/ru';

// Supported locales
export type Locale = 'en' | 'ru';

/** Языки интерфейса продукта (i18n-R04). */
const SUPPORTED_LOCALES: readonly Locale[] = ['ru', 'en'];

/**
 * Восстановленная локаль принимается, только если она — язык интерфейса.
 *
 * Приведение сохранённого значения к типу без проверки делало активным языком
 * произвольный код: интерфейс молча уходил в запасной английский по каждому
 * ключу, а переключатель показывал этот код.
 */
function restoreLocale(): Locale {
  const saved = localStorage.getItem('metabridge-locale');
  return SUPPORTED_LOCALES.find((locale) => locale === saved) ?? 'ru';
}

const defaultLocale: Locale = restoreLocale();

// Create i18n instance
export const i18n = createI18n({
  legacy: false, // Use Composition API mode
  locale: defaultLocale,
  fallbackLocale: 'en',
  messages: {
    en,
    ru,
  },
  globalInjection: true, // Enable global $t
});

// Языковой атрибут страницы соответствует активному языку с первого рендера,
// а не только после ручного переключения: иначе он врал бы о языке содержимого
// (i18n-R04) — а по нему ориентируются чтение с экрана и поисковые роботы.
document.documentElement.setAttribute('lang', defaultLocale);

// Helper to change locale
export function setLocale(locale: Locale): void {
  i18n.global.locale.value = locale;
  localStorage.setItem('metabridge-locale', locale);
  
  // Update HTML lang attribute
  document.documentElement.setAttribute('lang', locale);
}

// Get current locale
export function getCurrentLocale(): Locale {
  return i18n.global.locale.value;
}

// Export type for messages (for TypeScript autocomplete)
export type MessageSchema = typeof en;

// Export locales for language selector
export const availableLocales = [
  { code: 'en' as Locale, name: 'English', nativeName: 'English' },
  { code: 'ru' as Locale, name: 'Russian', nativeName: 'Русский' },
];

